import {
  BadgeCheck,
  CircleDollarSign,
  CreditCard,
  Handshake,
  Headset,
  Landmark,
  PackageCheck,
  Smartphone,
  Truck,
  Wallet,
} from "lucide-react";
import { WhatsAppIcon } from "../Logo";
import { waLink } from "../../api";

const BRANDS = [
  { name: "Razz Bar", model: "Razz Bar 60K" },
  { name: "Vap Solo", model: "Vap Solo Quads 80K" },
  { name: "Bang", model: "Bang 100K" },
  { name: "Happ Bar", model: "Happ Bar GR100K" },
  { name: "Rock Me", model: "Rock Me 25K" },
];

export function BrandShowcase() {
  return (
    <section id="marcas" data-testid="brands-section" className="px-4 py-16 lg:px-8">
      <div className="mx-auto max-w-7xl text-center">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#2EE6D6]">
          Los más solicitados
        </p>
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Nuestras 5 marcas principales
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-[#8A8F98] sm:text-base">
          Estos son los 5 modelos que más nos piden nuestros clientes. Trabajamos con muchos
          proveedores, así que si buscas cualquier otro modelo o marca, escríbenos por WhatsApp:
          te lo conseguimos y te encontramos el mejor precio.
        </p>
        <div className="mt-8 grid grid-cols-2 items-stretch justify-center gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {BRANDS.map((b) => (
            <div
              key={b.name}
              data-testid={`brand-badge-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex flex-col justify-center rounded-xl border border-[#2D323E] bg-[#1E2128] px-4 py-5 transition-all hover:-translate-y-1 hover:border-[#7C5CFF]/50"
            >
              <p className="font-heading text-base font-semibold text-[#F2F4F6]">{b.name}</p>
              <p className="mt-1 text-xs text-[#8A8F98]">{b.model}</p>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-xl text-xs leading-relaxed text-[#8A8F98]">
          ¿No ves el vaper que buscas? Tenemos acceso a cientos de modelos a través de nuestros
          proveedores. Dinos cuál quieres y te confirmamos disponibilidad y precio.
        </p>
        <a
          href={waLink("Hola NubeVape España, busco un modelo de vaper que no veo en la web. ¿Me lo podéis conseguir? Me interesa: ")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="brands-whatsapp-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 px-6 py-3 text-sm font-bold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
        >
          <WhatsAppIcon className="h-4 w-4" />
          Pedir otro modelo por WhatsApp
        </a>
      </div>
    </section>
  );
}

const TRUST_ITEMS = [
  {
    icon: Truck,
    title: "Envíos rápidos",
    text: "Entrega en 24/48h laborables en toda España, con seguimiento del pedido.",
  },
  {
    icon: Headset,
    title: "Atención directa",
    text: "Te atendemos por WhatsApp de forma personal, sin bots ni esperas.",
  },
  {
    icon: BadgeCheck,
    title: "Producto original",
    text: "Todos nuestros vapers son 100% originales, verificables por código.",
  },
  {
    icon: Wallet,
    title: "Pago flexible",
    text: "Tarjeta, Bizum, transferencia, PayPal o contrareembolso según tu zona.",
  },
];

export function TrustGuarantees() {
  return (
    <section id="confianza" data-testid="trust-section" className="bg-[#1E2128]/40 px-4 py-16 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="text-center">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#7C5CFF]">
            Compra con confianza
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Tu pedido, sin complicaciones
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_ITEMS.map((item) => (
            <div
              key={item.title}
              data-testid={`trust-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-[#2D323E] bg-[#1E2128] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#2EE6D6]/50"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#2EE6D6]/15 to-[#7C5CFF]/15">
                <item.icon className="h-5 w-5 text-[#2EE6D6]" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#8A8F98]">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PAYMENT_ITEMS = [
  { icon: CreditCard, title: "Tarjeta", note: "Pago seguro online" },
  { icon: Smartphone, title: "Bizum", note: "Instantáneo" },
  { icon: Landmark, title: "Transferencia", note: "Bancaria" },
  { icon: CircleDollarSign, title: "PayPal", note: "Con protección" },
  { icon: PackageCheck, title: "Contrareembolso", note: "Según zona" },
  { icon: Handshake, title: "Entrega en mano", note: "Según zona" },
];

export function PaymentDelivery() {
  return (
    <section id="pago" data-testid="payment-delivery-section" className="px-4 py-16 lg:px-8">
      <div className="mx-auto max-w-7xl text-center">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#2EE6D6]">
          Pago y entrega
        </p>
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          Paga como prefieras
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-[#8A8F98] sm:text-base">
          El método de pago y entrega se confirma por WhatsApp al hacer el pedido. Contrareembolso
          y entrega en mano disponibles según zona.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {PAYMENT_ITEMS.map((item) => (
            <div
              key={item.title}
              data-testid={`payment-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-[#2D323E] bg-[#1E2128] p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#7C5CFF]/50"
            >
              <item.icon className="mx-auto mb-3 h-6 w-6 text-[#7C5CFF]" />
              <p className="text-sm font-semibold">{item.title}</p>
              <p className="mt-1 text-xs text-[#8A8F98]">{item.note}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
